import * as Business   from '../business';
import * as FileEntry  from '../util/file_entry';
import * as User       from '../user';
import { connect }     from 'react-redux';
import { PageContent } from 'framework7-react';
import logEvent        from '../app/events/amplitude_log_event';
import MessageBar      from '../components/message_bar';
import MessagesList    from './messages_list';
import Navbar          from '../components/navbar';
import React           from 'react';
import useConversation from './use_conversation';
import WaitForTwilio   from './wait_for_twilio';


function Conversation({ business, conversationID, userID, users }) {
  if (!business)
    return null;

  return (
    <WaitForTwilio>
      <Page
        conversationID={conversationID}
        userID={userID}
        users={users}
      />
    </WaitForTwilio>
  );
}

export default connect(
  (state, { businessID, conversationID }) => ({
    conversationID,
    business: Business.getBusiness(state, businessID),
    userID:   User.getUserID(state),
    users:    Business.getUsers(state, businessID)
  })
)(Conversation);


function Page({ conversationID, userID, users }) {
  const otherParticipants = getOtherParticipants({ conversationID, userID, users });
  const conversation      = useConversation(conversationID, otherParticipants);
  const friendlyName      = getFriendlyName({ conversation, conversationID, otherParticipants });

  return (
    <React.Fragment>
      <Navbar title={friendlyName}/>
      <PageContent className="team-chat-page">
        <div className="conversation-view">
          {conversation &&
            <ConversationView
              conversation={conversation}
              friendlyName={friendlyName}
              userID={userID}
            />
          }
        </div>
      </PageContent>
    </React.Fragment>
  );
}

function ConversationView({ conversation, friendlyName, userID }) {
  const placeholder = friendlyName ? `Reply to ${friendlyName}` : '';

  async function sendMessage({ attachments, body }) {
    if (attachments) {
      for (const attachment of attachments) {
        const contentType = await FileEntry.getMimeTypeFromFileEntry(attachment);
        const media       = await FileEntry.getBlobFromFileEntry(attachment, contentType);
        await conversation.sendMessage({
          contentType,
          media
        });
      }
    }

    if (body)
      await conversation.sendMessage(body);

    logEvent('SentTeamChatMessage', {
      conversationID: conversation.sid,
      friendlyName
    });
  }

  function onKeyDown() {
    conversation.typing();
  }

  return (
    <MessageBar
      enabled
      onSend={sendMessage}
      paymentsEnabled={false}
      placeholder={placeholder}
      onKeyDown={onKeyDown}
    >
      {() => (
        <React.Fragment>
          <MessagesList
            userID={userID}
            conversation={conversation}
          />
          <TypingUsers
            conversation={conversation}
          />
        </React.Fragment>
      )}
    </MessageBar>
  );
}

function getFriendlyName({ conversation, conversationID, otherParticipants }) {
  const isRoom = conversationID.includes('business:');

  if (conversation?.friendlyName)
    return conversation.friendlyName;
  else if (isRoom)
    return '';
  else
    return otherParticipants.map(participant => participant.name).join(', ');
}

function getOtherParticipants({ conversationID, userID, users }) {
  const usersInConversation  = conversationID.replace('users:', '').split(':');
  const otherParticipantsIDs = usersInConversation.filter(id => id !== userID);
  const otherParticipants    = otherParticipantsIDs.map(id => users.find(user => user.id === id));
  return otherParticipants;
}

function TypingUsers({ conversation }) {
  const typingUsers   = useTypingUsers(conversation);
  const friendlyNames = typingUsers.map(user => user.friendlyName);

  function getText() {
    if (friendlyNames.length > 3)
      return 'Several people are typing';
    else {
      const verb = friendlyNames.length === 1 ? 'is' : 'are';
      return `${friendlyNames.join(', ')} ${verb} typing`;
    }
  }

  if (friendlyNames.length)
    return <span className="typing-users">{getText()}</span>;
  else
    return null;
}

function useTypingUsers(conversation) {
  const [ typingUsers, setTypingUsers ] = React.useState([]);

  React.useEffect(function() {
    async function onTypingStarted(user) {
      const { identity, friendlyName } = await user.getUserDescriptor();
      setTypingUsers(prevState => ([ ...prevState, { identity, friendlyName }]));
    }

    function onTypingEnded({ identity }) {
      setTypingUsers(prevState => {
        const filteredState = prevState.filter(user => user.identity !== identity);
        return filteredState;
      });
    }

    conversation.on('typingStarted', onTypingStarted);
    conversation.on('typingEnded', onTypingEnded);

    return function() {
      conversation.removeListener('typingStarted', onTypingStarted);
      conversation.removeListener('typingEnded', onTypingEnded);
    };
  }, [ conversation ]);

  return typingUsers;
}
